import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Minimal toast system (card 044).
 *
 * Why a custom one over a library: every other surface in the
 * dashboard renders its own primitives (ConfirmDialog, CopyableCode,
 * KindBadge…) and we'd rather keep the bundle small than pull in
 * react-hot-toast for a single use site.  This file is intentionally
 * < 250 lines including styles.
 *
 * Surface:
 *
 *   <ToastProvider>           — wrap the app once; the viewport is
 *                               rendered as a child portal-equivalent
 *                               div positioned top-right.
 *   useToast()                — returns `{ showToast, dismissToast }`.
 *
 *   const { showToast } = useToast();
 *   showToast({
 *     title: 'Plaintext: pk_live_jwN…',
 *     body: <YourCustomBody />,            // React node, optional
 *     variant: 'warning',                  // 'info' | 'success' | 'warning' | 'danger'
 *     autoHideMs: 30_000,                  // null → sticky; default 5_000
 *   });
 *
 * Queueing model: one toast on screen at a time.  Subsequent calls
 * push onto a FIFO queue and surface when the current toast hides
 * (auto or manual).  Avoids the "stack of seven floating cards"
 * problem cheap toast libraries produce on repeated clicks.
 *
 * Callers can also pre-empt the current toast by passing
 * ``replace: true`` — used by the reveal flow so clicking eye on a
 * second row immediately swaps the toast contents rather than
 * waiting for the first one to time out.
 */

const ToastCtx = createContext(null);

const DEFAULT_AUTO_HIDE_MS = 5000;

export function ToastProvider({ children }) {
  // Single visible toast + queue.  Storing { id, ...payload } lets us
  // address each one for dismissal even if two rapid calls share the
  // same title.
  const [current, setCurrent] = useState(null);
  const queueRef = useRef([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((idOrUndefined) => {
    setCurrent((prev) => {
      if (!prev) return prev;
      if (idOrUndefined !== undefined && prev.id !== idOrUndefined) {
        return prev;
      }
      // Pop the next queued toast (if any) into the current slot.
      const next = queueRef.current.shift() || null;
      return next;
    });
  }, []);

  const showToast = useCallback((payload) => {
    idRef.current += 1;
    const id = idRef.current;
    const item = {
      id,
      title: payload.title || '',
      body: payload.body || null,
      variant: payload.variant || 'info',
      autoHideMs: payload.autoHideMs === undefined
        ? DEFAULT_AUTO_HIDE_MS
        : payload.autoHideMs,
      // ``dismissible`` defaults true — the reveal flow needs an
      // explicit Hide button so we render the close-X regardless,
      // but bare info toasts can suppress it via dismissible: false.
      dismissible: payload.dismissible !== false,
    };
    setCurrent((prev) => {
      if (!prev || payload.replace) {
        // Drop any pending queue when replacing — the new toast is
        // the authoritative one.  Without this, eight rapid reveal
        // clicks would queue eight 30-second toasts back-to-back.
        if (payload.replace) queueRef.current = [];
        return item;
      }
      queueRef.current.push(item);
      return prev;
    });
    return id;
  }, []);

  const value = useMemo(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <ToastViewport toast={current} onDismiss={dismissToast} />
      <style>{TOAST_CSS}</style>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Defensive — if a caller forgets to wrap with the provider,
    // surface a clear error instead of silently no-op'ing the
    // showToast() call (the historic bug pattern for context-based
    // APIs that hand back stub fns when unmounted).
    throw new Error('useToast() must be called inside <ToastProvider>');
  }
  return ctx;
}

function ToastViewport({ toast, onDismiss }) {
  // Each visible toast schedules its own auto-hide timer.  Re-running
  // the effect on toast.id ensures the timer always belongs to the
  // currently-displayed toast, never a stale one from a previous render.
  useEffect(() => {
    if (!toast || !toast.autoHideMs) return undefined;
    const t = window.setTimeout(() => onDismiss(toast.id), toast.autoHideMs);
    return () => window.clearTimeout(t);
  }, [toast, onDismiss]);

  if (!toast) return null;
  return (
    <div className="toast-viewport" aria-live="polite" aria-atomic="true">
      <div
        className={`toast toast--${toast.variant}`}
        role="status"
        // ``key`` forces the slide-in animation to replay when a new
        // toast replaces an old one — same DOM node otherwise → no
        // animation re-trigger.
        key={toast.id}
      >
        <div className="toast__head">
          <div className="toast__title">{toast.title}</div>
          {toast.dismissible && (
            <button
              type="button"
              className="toast__close"
              onClick={() => onDismiss(toast.id)}
              title="Dismiss"
              aria-label="Dismiss toast"
            >
              <CloseIcon />
            </button>
          )}
        </div>
        {toast.body && <div className="toast__body">{toast.body}</div>}
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const TOAST_CSS = `
  .toast-viewport {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 2000;
    pointer-events: none;
  }
  .toast {
    width: 380px;
    max-width: calc(100vw - 32px);
    background: var(--surface-0, #fff);
    color: var(--text-1, #0f172a);
    border: 1px solid var(--border-1, #e2e8f0);
    border-left: 4px solid var(--brand-600, #4f46e5);
    border-radius: 10px;
    box-shadow: 0 18px 36px -16px rgba(15, 23, 42, 0.35);
    padding: 12px 14px;
    pointer-events: auto;
    animation: toast-slide-in 180ms ease-out;
  }
  .toast--success { border-left-color: var(--success-600, #16a34a); }
  .toast--warning { border-left-color: var(--warn-700, #b45309); }
  .toast--danger  { border-left-color: var(--danger-700, #b91c1c); }
  .toast__head {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .toast__title {
    flex: 1;
    font-weight: 600;
    font-size: 13.5px;
    line-height: 1.35;
    word-break: break-word;
  }
  .toast__close {
    flex-shrink: 0;
    background: transparent;
    border: 0;
    padding: 4px;
    border-radius: 6px;
    color: var(--text-3, #64748b);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.12s, color 0.12s;
  }
  .toast__close:hover {
    background: var(--surface-1, #f1f5f9);
    color: var(--text-1, #0f172a);
  }
  .toast__body {
    margin-top: 8px;
    font-size: 13px;
    line-height: 1.45;
    color: var(--text-2, #334155);
  }
  @keyframes toast-slide-in {
    from {
      opacity: 0;
      transform: translateX(16px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
